"use client";

import { motion } from "framer-motion";
import {
  Users,
  TrendingUp,
  CheckCircle2,
  Tag,
  Phone,
  Handshake,
  Pencil,
  Trash2,
  AlertTriangle,
  UserCheck,
  Filter,
  RefreshCw,
} from "lucide-react";

function MulplyLogo() {
  return (
    <>
      <img src="/logotipo_gradient.svg" alt="Mulply" className="h-4 w-auto dark:hidden" />
      <img src="/logotipo_white.svg" alt="Mulply" className="h-4 w-auto hidden dark:block" />
    </>
  );
}

const TYPE_CLS: Record<string, string> = {
  Influenciador: "bg-[#ec399a]/10 text-[#ec399a] border-[#ec399a]/20",
  Afiliado:      "bg-[#48c1ce]/10 text-[#48c1ce] border-[#48c1ce]/20",
  Revendedor:    "bg-amber-500/10 text-amber-600 border-amber-500/20",
  Embaixador:    "bg-purple-500/10 text-purple-600 border-purple-500/20",
};

const AVATAR_CLS: string[] = [
  "bg-[#ec399a]/20 text-[#ec399a]",
  "bg-[#48c1ce]/20 text-[#48c1ce]",
  "bg-amber-500/20 text-amber-600",
  "bg-purple-500/20 text-purple-600",
];

const partners = [
  { name: "Ana Fernanda",  type: "Influenciador", coupon: "ANA15",  phone: "(11) 98765-0001", country: "br", active: true,  deals: 18, avatar: "AF" },
  { name: "Lucas Brandão", type: "Afiliado",       coupon: "LB10",   phone: "(21) 97654-0002", country: "br", active: true,  deals:  9, avatar: "LB" },
  { name: "Carla Matos",   type: "Revendedor",     coupon: "CM20",   phone: "(31) 96543-0003", country: "br", active: false, deals:  4, avatar: "CM" },
  { name: "Diego Ruiz",    type: "Embaixador",     coupon: "DR05",   phone: "+34 612 345 678", country: "es", active: true,  deals: 31, avatar: "DR" },
];

function PartnersMock() {
  return (
    <div className="relative bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-2xl border border-gray-200/60 dark:border-white/10 shadow-xl overflow-hidden w-full">
      {/* Browser chrome */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-white/10 bg-gray-50/50 dark:bg-white/5">
        <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        <MulplyLogo />
        <div className="ml-auto flex items-center gap-1.5">
          <button className="flex items-center gap-1 px-2 py-1 rounded-md border border-gray-200 dark:border-white/10 text-[10px] font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10">
            <Filter className="w-2.5 h-2.5" /> Filtro
          </button>
          <button className="flex items-center gap-1 px-2 py-1 rounded-md border border-gray-200 dark:border-white/10 text-[10px] font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10">
            <RefreshCw className="w-2.5 h-2.5" /> Sync
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-4 gap-px bg-gray-100/60 dark:bg-white/5 border-b border-gray-100 dark:border-white/10">
        {[
          { icon: Users,         label: "REDE DE PARCEIROS", value: "4",      color: "text-[#48c1ce]" },
          { icon: UserCheck,     label: "PARCEIROS ATIVOS",  value: "3",      color: "text-emerald-600" },
          { icon: TrendingUp,    label: "EFICÁCIA",          value: "94%",    color: "text-amber-600"  },
          { icon: AlertTriangle, label: "ATENÇÃO",           value: "1",      color: "text-red-600"    },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="bg-white/80 dark:bg-gray-900/80 px-3 py-2.5 flex flex-col gap-0.5">
            <div className="flex items-center gap-1">
              <Icon className={`w-3 h-3 ${color}`} />
              <span className="text-[8px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide">{label}</span>
            </div>
            <span className={`text-base font-black tabular-nums ${color}`}>{value}</span>
          </div>
        ))}
      </div>

      {/* Table header */}
      <div className="grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-x-3 px-4 py-2 bg-gray-50/80 dark:bg-white/5 border-b border-gray-100 dark:border-white/10">
        {["PARCEIRO", "TELEFONE", "PAÍS", "STATUS", "PARCERIAS", ""].map((h) => (
          <span key={h} className="text-[8px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.1em]">{h}</span>
        ))}
      </div>

      {/* Table rows */}
      <div className="divide-y divide-gray-100 dark:divide-white/5">
        {partners.map((p, i) => (
          <div key={p.name} className="grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-x-3 items-center px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
            {/* Parceiro */}
            <div className="flex items-center gap-2 min-w-0">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-black flex-shrink-0 ${AVATAR_CLS[i % AVATAR_CLS.length]}`}>
                {p.avatar}
              </div>
              <div className="min-w-0">
                <div className="text-[11px] font-bold text-gray-800 dark:text-gray-200 truncate">{p.name}</div>
                <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[8px] font-bold uppercase tracking-[0.1em] ${TYPE_CLS[p.type]}`}>
                    <Tag className="w-2 h-2" />{p.type}
                  </span>
                  <span className="text-[8px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 font-semibold tracking-wide">
                    {p.coupon}
                  </span>
                </div>
              </div>
            </div>

            {/* Telefone */}
            <div className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400 font-medium">
              <Phone className="w-2.5 h-2.5 text-gray-300 dark:text-gray-600" />
              <span className="whitespace-nowrap">{p.phone}</span>
            </div>

            {/* País */}
            <div className="flex items-center gap-1.5">
              <img
                src={`https://flagcdn.com/w20/${p.country}.png`}
                srcSet={`https://flagcdn.com/w40/${p.country}.png 2x`}
                width="14"
                alt={p.country.toUpperCase()}
                className="rounded-[1.5px] shadow-[0_0_0_1px_rgba(0,0,0,0.1)] object-cover"
              />
              <span className="text-[9px] font-semibold tracking-[0.1em] text-gray-400 dark:text-gray-500 uppercase">{p.country}</span>
            </div>

            {/* Status */}
            <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-semibold uppercase tracking-[0.1em] border ${
              p.active
                ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
                : "bg-red-500/10 text-red-600 border-red-500/20"
            }`}>
              <div className={`w-1.5 h-1.5 rounded-full ${p.active ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
              {p.active ? "Ativo" : "Inativo"}
            </div>

            {/* Parcerias */}
            <div className="flex flex-col items-center">
              <span className="text-sm font-semibold text-[#48c1ce] tabular-nums">{p.deals}</span>
              <span className="text-[8px] text-gray-400 dark:text-gray-500">Parcerias</span>
            </div>

            {/* Decisões */}
            <div className="flex items-center gap-1">
              <button className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
                <Pencil className="w-3 h-3 text-gray-400" />
              </button>
              <button className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-red-500/10 transition-colors">
                <Trash2 className="w-3 h-3 text-gray-400 hover:text-red-500" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PartnersLP3() {
  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50/50 dark:bg-white/[0.02] border-y border-gray-100 dark:border-white/10 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
          {/* Text */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: "easeOut" as const }}
            className="flex-1 flex flex-col gap-5"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#ec399a]/30 bg-[#ec399a]/10 text-[#ec399a] text-xs font-bold w-fit">
              <Handshake className="w-3 h-3" />
              Gestão de Parceiros
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-gray-900 dark:text-white leading-tight">
              Parceiros e influenciadores
              <br />
              <span className="bg-gradient-to-r from-primary via-purple-600 to-pink-600 bg-clip-text text-transparent">
                organizados de verdade.
              </span>
            </h2>

            <p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed max-w-lg">
              Um painel unificado para cadastrar, segmentar e acompanhar todos os seus parceiros — influenciadores, afiliados, revendedores e embaixadores — com status, país e histórico de parcerias em uma única tela.
            </p>

            <ul className="flex flex-col gap-2.5">
              {[
                "Cadastro por tipo: Influenciador, Afiliado, Revendedor, Embaixador",
                "Cupom de desconto vinculado a cada parceiro",
                "Bandeira do país e telefone direto no painel",
                "Status Ativo/Inativo com contagem de parcerias",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm font-semibold text-gray-700 dark:text-gray-200">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#ec399a]" />
                  {b}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Mock */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, ease: "easeOut" as const }}
            className="flex-1 flex justify-center w-full"
          >
            <div className="relative w-full max-w-2xl">
              <div className="absolute inset-0 bg-gradient-to-r from-[#ec399a]/20 to-[#48c1ce]/20 blur-3xl rounded-3xl" />
              <div className="relative">
                <PartnersMock />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
